import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { Router, RouterModule } from '@angular/router';
import { _, TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { AppMenuItem, menuItems, menuLabels } from './app.navigation';

import { toSignal } from '@angular/core/rxjs-interop';
import { SanctumAuthService } from '@freesixtyfive/auth';
import { SidebarLayoutComponent } from '@fsf/ui';
import { map } from 'rxjs';
import { links } from './theme/app-links';
import { UserService } from './shared/services/user.service';
import { MenuItem } from 'primeng/api';

_([
  'meta.title',
  'meta.description',
  'meta.keywords',
  'meta.ogTitle',
  'meta.ogDescription',
  'meta.ogSiteName',
]);

@Component({
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    ButtonModule,
    TooltipModule,
    SidebarLayoutComponent,
  ],
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  #router = inject(Router);
  #meta = inject(Meta);
  #translate = inject(TranslateService);
  #auth = inject(SanctumAuthService);
  #user = inject(UserService);

  isAuthenticated = this.#auth.isAuth;
  links = links;

  menuItems = toSignal(
    this.#translate
      .stream(menuLabels)
      .pipe(
        map((translations) =>
          menuItems.map((item) => this.translateMenuItem(item, translations)),
        ),
      ),
    { initialValue: [] },
  );

  items = computed(() => {
    if (!this.#auth.isAuth()) return [];
    const perms = this.#auth.permissions();
    return this.filterMenuItems(this.menuItems(), perms);
  });

  ngOnInit(): void {
    this.#translate
      .get([
        'meta.title',
        'meta.description',
        'meta.keywords',
        'meta.ogTitle',
        'meta.ogDescription',
        'meta.ogSiteName',
      ])
      .subscribe((metaTranslations) => {
        this.setMetaTags(metaTranslations);
      });
  }

  logout(): void {
    this.#auth.signOut().subscribe(() => {
      this.#user.clearUser();
      this.#router.navigate(['/login']);
    });
  }

  private filterMenuItems(
    items: AppMenuItem[],
    perms: string[],
  ): AppMenuItem[] {
    return items
      .map((item) => {
        if (item.items && item.items.length > 0) {
          const filteredChildren = this.filterMenuItems(
            item.items as AppMenuItem[],
            perms,
          );
          if (filteredChildren.length === 0) return null;
          return { ...item, items: filteredChildren };
        }
        if (item.permission && !perms.includes(item.permission)) return null;
        return item;
      })
      .filter((item): item is AppMenuItem => item !== null);
  }

  private setMetaTags(metaTranslations: Record<string, string>): void {
    if (
      this.#meta.getTag('name="title"') &&
      this.#meta.getTag('name="description"') &&
      this.#meta.getTag('name="keywords"') &&
      this.#meta.getTag('property="og:title"') &&
      this.#meta.getTag('property="og:description"') &&
      this.#meta.getTag('property="og:site_name"')
    ) {
      this.#meta.updateTag(
        { content: metaTranslations['meta.title'] },
        'name="title"',
      );
      this.#meta.updateTag(
        { content: metaTranslations['meta.description'] },
        'name="description"',
      );
      this.#meta.updateTag(
        { content: metaTranslations['meta.keywords'] },
        'name="keywords"',
      );
      this.#meta.updateTag(
        { content: metaTranslations['meta.ogTitle'] },
        'property="og:title"',
      );
      this.#meta.updateTag(
        { content: metaTranslations['meta.ogDescription'] },
        'property="og:description"',
      );
      this.#meta.updateTag(
        { content: metaTranslations['meta.ogSiteName'] },
        'property="og:site_name"',
      );
    }
  }

  private translateMenuItem(
    item: MenuItem,
    translations: Record<string, string>,
  ): MenuItem {
    const translatedItem: MenuItem = {
      ...item,
      label: translations[item.label as string] || item.label,
    };

    if (item.items && item.items.length > 0) {
      translatedItem.items = item.items.map((subItem) =>
        this.translateMenuItem(subItem, translations),
      );
    }

    return translatedItem;
  }
}
